import { useState, FormEvent } from "react";

const FIELDS = [
  { name: "金属加工", },
  { name: "樹脂成形" },
  { name: "ガラス加工" },
  { name: "蒸着・コーティング" },
  { name: "組立・検査工程" },
  { name: "その他" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    // honeypot
    const hp = (data.get("bot-field") as string | null)?.trim() ?? "";
    if (hp) {
      // pretend success
      setStatus("success");
      form.reset();
      return;
    }
    data.delete("bot-field");
    data.set("form-name", "contact");

    // basic validation
    const email = (data.get("email") as string | null)?.trim() ?? "";
    const name = (data.get("name") as string | null)?.trim() ?? "";
    const message = (data.get("message") as string | null)?.trim() ?? "";
    if (!name || !email || !message) {
      setStatus("error");
      setErrorMessage("お名前・メールアドレス・ご相談内容は必須項目です。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setErrorMessage("ご相談内容は500文字以内でお願いします。");
      return;
    }

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") params.append(key, value);
    });

    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      if (!response.ok) {
        setStatus("error");
        setErrorMessage("送信に失敗しました。時間をおいて再度お試しください。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setErrorMessage("通信エラーが発生しました。ネットワーク環境をご確認のうえ、再度お試しください。");
    }
  };

  return (
    <section id="contact" className="w-full py-24 md:py-32 bg-primary-950 text-background-50 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-25 pointer-events-none bg-cover bg-center"
        style={{
          backgroundImage:
            "url('/images/contact.svg')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/90 to-primary-950 pointer-events-none" />

      <div className="relative w-full max-w-5xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-[1px] bg-accent-300" />
              <span className="text-[11px] tracking-[0.35em] text-accent-100 uppercase">Contact</span>
            </div>
            <h4 className="font-heading text-3xl md:text-4xl leading-snug mb-6">
              <a href="#contact" className="cursor-pointer">まずは、<br />状況をお聞かせください</a>
            </h4>
            <p className="text-sm md:text-base text-background-200 leading-relaxed mb-10">
              初回のヒアリングで、現在起きている現象や工程の状況を確認します。
              そのうえで、対応可能か、調査を進める価値がある案件かを率直にお伝えします。
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-800 text-accent-200">
                  <i className="ri-time-line"></i>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.2em] text-accent-100 mb-1">RESPONSE</div>
                  <div className="text-sm text-background-100">通常2営業日以内にご返信</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-800 text-accent-200">
                  <i className="ri-shield-check-line"></i>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.2em] text-accent-100 mb-1">CONFIDENTIALITY</div>
                  <div className="text-sm text-background-100">NDA締結対応可能</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-800 text-accent-200">
                  <i className="ri-price-tag-3-line"></i>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.2em] text-accent-100 mb-1">FIRST TALK</div>
                  <div className="text-sm text-background-100">初回ヒアリングは無料</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-background-50 text-foreground-900 rounded-lg p-6 md:p-10">
              {status === "success" ? (
                <div className="py-8 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-5">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h5 className="font-heading text-xl text-foreground-900 mb-3">
                    お問い合わせを受け付けました
                  </h5>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    ご送信ありがとうございます。
                    <br />
                    2営業日以内にご連絡いたします。
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 border border-background-300 hover:border-primary-400 text-foreground-700 text-sm rounded-md cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    別のお問い合わせを送る
                  </button>
                </div>
              ) : (
                <form
                  id="contact-form"
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={submit}
                  className="space-y-5"
                  noValidate
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs text-foreground-700 mb-2">
                        お名前 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-3 text-sm bg-background-100 border border-background-300 rounded-md focus:outline-none focus:border-primary-500"
                        placeholder="山田 太郎"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-700 mb-2">
                        貴社名
                      </label>
                      <input
                        type="text"
                        name="company"
                        className="w-full px-4 py-3 text-sm bg-background-100 border border-background-300 rounded-md focus:outline-none focus:border-primary-500"
                        placeholder="株式会社◯◯製作所"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs text-foreground-700 mb-2">
                        メールアドレス <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 text-sm bg-background-100 border border-background-300 rounded-md focus:outline-none focus:border-primary-500"
                        placeholder="taro@example.co.jp"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-700 mb-2">
                        電話番号
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        className="w-full px-4 py-3 text-sm bg-background-100 border border-background-300 rounded-md focus:outline-none focus:border-primary-500"
                        placeholder="03-0000-0000"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-foreground-700 mb-2">
                      対象分野（任意・複数可）
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {FIELDS.map((f) => (
                        <label
                          key={f.name}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-300 rounded-md text-xs text-foreground-700 hover:border-primary-400 cursor-pointer has-[:checked]:bg-primary-500 has-[:checked]:text-background-50 has-[:checked]:border-primary-500"
                        >
                          <input type="checkbox" name="fields" value={f.name} className="sr-only" />
                          {f.name}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-foreground-700 mb-2">
                      ご相談内容 <span className="text-primary-500">*</span>
                      <span className="text-foreground-400 ml-2">（500文字以内）</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 text-sm bg-background-100 border border-background-300 rounded-md focus:outline-none focus:border-primary-500 resize-none"
                      placeholder="発生している不良現象、工程、これまで試された対策などをご記入ください。"
                    />
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="bot-field"
                    className="ant-field-alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  {status === "error" && errorMessage && (
                    <div className="flex items-start gap-3 p-4 bg-background-100 border border-primary-300 rounded-md">
                      <div className="w-6 h-6 shrink-0 flex items-center justify-center text-primary-600">
                        <i className="ri-error-warning-line"></i>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">{errorMessage}</p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <p className="text-[11px] text-foreground-500 leading-relaxed">
                      ご相談内容を確認のうえ、対応可否をご連絡します。
                      <br />
                      ご相談いただいた時点で費用が発生することはありません。
                      <br />
                      ご入力いただいた情報は、お問い合わせへの対応・連絡の目的にのみ利用します。
                    </p>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "loading" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          送信中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          送信する
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}